import React, { useState, useEffect } from "react";
import { API_BASE_URL, ROLES, COUNTRY_CODES } from "./constants/adminConstants";
import AdminOtpVerification from "./AdminOtpVerification";
import PasswordStrengthBar from "./PasswordStrengthBar";
import "./css/adminStyles.css";

function AdminSignUp() {
  const [step, setStep] = useState("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginId, setLoginId] = useState("");
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (firstName && role) {
      const generated = lastName
        ? `${firstName}${lastName}.${role}TVC.com`
        : `${firstName}.${role}TVC.com`;
      setLoginId(generated);
    } else {
      setLoginId("");
    }
  }, [firstName, lastName, role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!firstName || !email || !role || !password) {
      setError("Please fill in all required fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          countryCode,
          role,
          password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Signup failed.");
        setLoading(false);
        return;
      }

      setUserId(data.userId);
      setStep("otp");
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = () => {
    setStep("done");
  };

  const handleOtpCancel = () => {
    setStep("form");
    setUserId(null);
  };

  if (step === "done") {
    return (
      <div className="AdminLoginPage">
        <div className="AdminLoginBox">
          <h1 className="AdminLoginTitle">Registration Complete</h1>
          <p className="AdminLoginSubtitle">
            Your account has been created successfully. An admin will review your
            application and activate your account.
          </p>
          <p className="AdminLoginSubtitle" style={{ marginTop: 16 }}>
            You will be notified once your account is approved.
          </p>
          <a href="/admin/login" className="AdminButton" style={{ textAlign: "center", display: "block", textDecoration: "none" }}>
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="AdminLoginPage">
      <div className="AdminLoginBox">
        <h1 className="AdminLoginTitle">Create Account</h1>

        <form onSubmit={handleSubmit}>
          <div className="AdminRow">
            <div className="AdminFormGroup">
              <label className="AdminLabel">First Name</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Last Name</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="AdminRow">
            <div className="AdminFormGroup">
              <label className="AdminLabel">Phone Number</label>
              <div className="AdminCountryCodeRow">
                <div className="AdminCountryCodeSelect">
                  <div className="AdminSelect AdminCountryCodeDisplay">
                    {countryCode}
                  </div>
                  <select
                    className="AdminSelect AdminCountryCodeNative"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.value + c.label} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="AdminPhoneInput">
                  <input
                    className="AdminInput"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Email</label>
              <input
                className="AdminInput"
                type="email"
                placeholder="john.doe@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="AdminFormGroup">
            <label className="AdminLabel">Role</label>
            <select
              className="AdminSelect"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <option value="">Select a role</option>
              <option value="Admin">Admin</option>
              <option value="Manager">Manager</option>
              <option value="Staff">Staff</option>
            </select>
          </div>

          <div className="AdminRow">
            <div className="AdminFormGroup">
              <label className="AdminLabel">Password</label>
              <div className="AdminPwInputWrap">
                <input
                  className="AdminInput AdminPwInput"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="button" className="AdminPwToggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              <PasswordStrengthBar password={password} />
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Confirm Password</label>
              <div className="AdminPwInputWrap">
                <input
                  className={`AdminInput AdminPwInput ${confirmPassword && password !== confirmPassword ? "AdminInputError" : ""}`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button type="button" className="AdminPwToggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}>
                  {showConfirmPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="AdminFieldError">Passwords do not match.</p>
              )}
            </div>
          </div>

          {loginId && (
            <div className="AdminFormGroup">
              <label className="AdminLabel">Generated Login ID</label>
              <div className="AdminGeneratedIdBox">
                <div className="AdminGeneratedIdValue">{loginId}</div>
              </div>
            </div>
          )}

          {error && <p className="AdminError">{error}</p>}

          {step === "form" && (
            <button className="AdminButton" type="submit" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          )}
        </form>

        {step === "otp" && userId && (
          <AdminOtpVerification userId={userId} onVerified={handleOtpVerified} onCancel={handleOtpCancel} />
        )}

        <div className="AdminTextCenter">
          <a href="/admin/login" className="AdminLink">Already have an account? Sign in</a>
        </div>
      </div>
    </div>
  );
}

export default AdminSignUp;
